import { Delete, Save } from '@mui/icons-material';
import { Button, styled, type ButtonProps } from '@mui/material';
import { forwardRef } from 'react';
import { useTranslation } from 'react-i18next';
import { IconButtonTooltip, type IconButtonLabelProps } from './IconButton';
import { TableAction, TableActionLink } from './table';

export const ActionButton = styled(TableAction)(({ theme }) => ({
  padding: theme.spacing(1),
  [theme.breakpoints.down('sm')]: {
    padding: theme.spacing(0.5),
  },
}));

export const ActionButtonLink = styled(TableActionLink)(({ theme }) => ({
  padding: theme.spacing(1),
  [theme.breakpoints.down('sm')]: {
    padding: theme.spacing(0.5),
  },
}));

const StyledOutlinedIconButton = styled(Button)(({ theme }) => ({
  padding: '6px 0px !important',
  minWidth: '45px',
  '& .MuiButton-startIcon': {
    marginLeft: '0px',
    marginRight: '0px',
  },
  '&.MuiButton-outlinedPrimary': {
    color: theme.palette.common.black,
  },
  '&.Mui-disabled': {
    color: theme.palette.text.disabled,
  },
  '&.MuiButton-containedPrimary': {
    color: theme.palette.common.white,
    border: 0,
    '&:hover': {
      backgroundColor: theme.palette.primary.darkHover,
      border: 0,
    },
  },
  border: `1px solid ${theme.palette.borders.control}`,
  '&:hover': {
    backgroundColor: theme.palette.background.default,
    boxShadow: 'none',
    border: `1px solid ${theme.palette.borders.control}`,
  },
}));

export type OutlinedIconButtonProps = Omit<ButtonProps, 'aria-label' | 'title'> &
  IconButtonLabelProps;

/** A bordered icon-only button. Like `~/components/IconButton`, `label` names it and titles its tooltip. */
export const OutlinedIconButton = forwardRef<HTMLButtonElement, OutlinedIconButtonProps>(
  function OutlinedIconButton({ description, label, tooltipPlacement, ...props }, ref) {
    return (
      <IconButtonTooltip
        description={description}
        label={label}
        tooltipPlacement={tooltipPlacement}
      >
        <StyledOutlinedIconButton
          size="medium"
          variant="outlined"
          {...props}
          aria-label={label}
          ref={ref}
        />
      </IconButtonTooltip>
    );
  },
);

export const SaveButton = forwardRef<HTMLButtonElement, ButtonProps>(
  //
  function SaveButton(props, ref) {
    const { t } = useTranslation();
    const { children, ...restProps } = props;
    const label = children ?? t('common.actions.save');
    return (
      <Button
        color="primary"
        size="medium"
        startIcon={<Save />}
        variant="contained"
        {...restProps}
        ref={ref}
      >
        {label}
      </Button>
    );
  },
);

const StyledDeleteButton = styled(Button)(({ theme }) => ({
  backgroundColor: theme.palette.error.main,
  border: `1px solid ${theme.palette.error.main}`,
  color: theme.palette.error.contrastText,
  '&:hover': {
    backgroundColor: theme.palette.error.dark,
  },
  '&.Mui-disabled': {
    border: '1px solid rgb(224 224 224)',
    '& .MuiButton-startIcon': {
      color: 'rgba(0, 0, 0, 0.26)',
    },
  },
  '& .MuiButton-startIcon': {
    color: theme.palette.error.contrastText,
  },
}));

export const DeleteButton = forwardRef<HTMLButtonElement, ButtonProps>(
  function DeleteButton(props, ref) {
    const { t } = useTranslation();
    const { children, ...restProps } = props;
    const label = children ?? t('common.actions.delete');
    return (
      <StyledDeleteButton
        size="medium"
        startIcon={<Delete />}
        variant="contained"
        {...restProps}
        ref={ref}
      >
        {label}
      </StyledDeleteButton>
    );
  },
);
